"use client";

import Link from "next/link";
import { useEffect, useState, use } from "react";

type Article = {
  title: string;
  slug: string;
  summary?: string;
  content: string;
  level?: string;
  category?: string;
  seo_keywords?: string[];
  status?: string;
  author?: string;
  updated_at?: string;
};

interface ArticlePageProps {
  params: Promise<{ slug: string }>;
}

export default function LearningArticlePage({ params }: ArticlePageProps) {
  const { slug } = use(params);
  const [article, setArticle] = useState<Article | null>(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);

  useEffect(() => {
    const load = async () => {
      try {
        const res = await fetch(`/api/articles/${slug}`, { cache: "no-store" });
        if (!res.ok) {
          throw new Error(`HTTP ${res.status}`);
        }
        const data = await res.json();
        setArticle(data.article || null);
      } catch (err) {
        setError(err instanceof Error ? err.message : "Failed to load article");
      } finally {
        setLoading(false);
      }
    };
    load();
  }, [slug]);

  if (loading) {
    return (
      <div style={{ textAlign: "center", padding: "2rem" }}>
        <p>Loading article...</p>
      </div>
    );
  }

  if (error || !article) {
    return (
      <div style={{ textAlign: "center", padding: "2rem", color: "var(--color-text-muted)" }}>
        <p>Article not found.</p>
        <Link href="/learning">Back to Learning</Link>
      </div>
    );
  }

  return (
    <>
      <section className="page-header page-header--accent">
        <div className="page-header-container">
          <div style={{ marginBottom: "0.75rem" }}>
            <Link href="/learning" className="back-link">
              ← Back to Learning
            </Link>
          </div>
          <h1 className="page-title">{article.title}</h1>
          <p className="page-subtitle">
            {article.summary || "Practical PSX learning guide."}
          </p>
          <div style={{ display: "flex", gap: "1rem", flexWrap: "wrap", marginTop: "1rem", color: "var(--color-text-muted)" }}>
            {article.level && <span>Level: {article.level}</span>}
            {article.author && <span>Author: {article.author}</span>}
            {article.updated_at && <span>Updated: {new Date(article.updated_at).toLocaleDateString()}</span>}
          </div>
        </div>
      </section>

      <section className="learning-section">
        <div className="learning-container">
          <article style={{ lineHeight: 1.7, fontSize: "1rem", whiteSpace: "pre-wrap" }}>
            {article.content}
          </article>

          {Array.isArray(article.seo_keywords) && article.seo_keywords.length > 0 && (
            <div style={{ marginTop: "2rem" }}>
              <h3>Keywords</h3>
              <div style={{ display: "flex", gap: "0.5rem", flexWrap: "wrap" }}>
                {article.seo_keywords.map((k) => (
                  <span key={k} style={{ padding: "0.3rem 0.6rem", borderRadius: "999px", background: "rgba(59, 130, 246, 0.1)", color: "var(--blue-text)", fontSize: "0.85rem" }}>
                    {k}
                  </span>
                ))}
              </div>
            </div>
          )}
        </div>
      </section>
    </>
  );
}
